import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import AnimatedMainContainer from "../../../components/AnimatedMainContainer";
import { useToast } from "../../../hooks/toast";
import api from "../../../service";
import InputWithLabel from "../../../components/InputWithLabel";
import { Form } from "@unform/web";

import {
  Container,
  FormContainer,
  TitleContainer,
  Title,
  FieldsContainer,
  Horizontal,
  LinkButton,
  GoBackContainer,
  BadgeContainer,
  FilterActionsContainer,
  SubmitContainer,
} from "./styles";
import SelectWithLabel from "../../../components/SelectWithLabel";
import Table from "../../../components/Table";
import Badge from "../../../components/Badge";
import { useLoading } from "../../../hooks/loading";
import FormButton from "../../../components/FormButton";

const ServiceProviderAssociation = () => {
  const { token } = useSelector((state) => state.auth);
  const [filteredData, setFilteredData] = useState([]);
  const [services, setServices] = useState([]);
  const [events, setEvents] = useState([]);
  const formRef = useRef(null);
  const intl = useIntl();
  const history = useHistory();
  const { addToast } = useToast();
  const { handleLoading } = useLoading();
  const [loading, setLoading] = useState(false);

  const handleSubmit = (data) => {
    setLoading(true);
    if (loading) {
      return;
    }
    api
      .patch(
        "/serviceprovider/filter",
        {
          eventId: data.event === "" ? null : data.event,
          provisionType: data.serviceType === "" ? null : data.serviceType,
          boothNumber: data.booth === "" ? null : data.booth,
          accountName: data.exhibitor === "" ? null : data.exhibitor,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((result) => {
        const data = result.data.output;
        const translatedData = (data || []).map((item) => ({
          ...item,
          serviceType: item.provisionTypeName,
        }));

        setLoading(false);

        setFilteredData(translatedData);
      })
      .catch((err) => {
        setLoading(false);
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "api.error." + err.response.data.code,
          }),
        });
      });
  };

  const getServiceProviderAssociations = useCallback(() => {
    handleLoading(true);
    api
      .patch(
        "/serviceprovider/filter",
        {
          eventId: null,
          provisionType: null,
          boothNumber: null,
          accountName: null,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((result) => {
        const data = result.data.output;
        const translatedData = (data || []).map((item) => ({
          ...item,
          serviceType: item.provisionTypeName,
        }));

        setFilteredData(translatedData);
      })
      .catch((err) => {
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "api.error." + err.response.data.code,
          }),
        });
      })
      .finally(() => {
        handleLoading(false);
      });
  }, [addToast, intl, token, handleLoading]);

  const getServices = useCallback(() => {
    handleLoading(true);
    api
      .get("/serviceprovider/services", {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((result) => {
        const translatedServices = result.data.provisionTypes
          .map((item) => ({
            value: item.provisionType,
            label: item.provisionTypeName,
          }))
          .sort((a, b) => (a.label || "").localeCompare(b.label || ""));

        setServices(translatedServices);
      })
      .catch((err) => {
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "api.error." + err.response.data.code,
          }),
        });
      })
      .finally(() => {
        handleLoading(false);
      });
  }, [addToast, intl, token, handleLoading]);

  const getEvents = useCallback(() => {
    handleLoading(true);
    api
      .get("/serviceprovider/events", {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((result) => {
        const data = result.data.events;
        let eventsSort = [...data].sort((a, b) =>
          a.eventName < b.eventName ? -1 : a.eventName > b.eventName ? 1 : 0
        );
        setEvents(eventsSort);
      })
      .catch((err) => {
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "api.error." + err.response.data.code,
          }),
        });
      })
      .finally(() => {
        handleLoading(false);
      });
  }, [addToast, intl, token, handleLoading]);

  useEffect(() => {
    getServiceProviderAssociations();
    getServices();
    getEvents();
  }, [getEvents, getServiceProviderAssociations, getServices]);

  const columns = useMemo(
    () => [
      {
        name: intl.formatMessage({ id: "event_edition" }),
        selector: (row) => row.eventEditionName,
      },
      {
        name: intl.formatMessage({ id: "exhibitor" }),
        selector: (row) => row.accountName,
      },
      {
        name: intl.formatMessage({ id: "type" }),
        selector: (row) => row.serviceType,
      },
      {
        name: intl.formatMessage({ id: "booth" }),
        selector: (row) => row.boothNumber,
      },
      {
        cell: (row) => (
          <Badge
            color="#002244"
            text={intl.formatMessage({ id: "see_details" })}
            onClick={() =>
              history.push({
                pathname: "/serviceprovider-association-provider-detail",
                state: {
                  opportunityId: row.opportunityIdNumber,
                  provisionType: row.provisionType,
                },
              })
            }
            style={{ cursor: "pointer" }}
          />
        ),
      },
    ],
    [history, intl]
  );

  const clearAll = () => {
    formRef.current.setFieldValue("event", null);
    formRef.current.setFieldValue("serviceType", null);
    formRef.current.reset();
    getServiceProviderAssociations();
  };

  return (
    <>
      <AnimatedMainContainer
        breadcrumb={intl.formatMessage({ id: "breadcrumb.events" })}
      >
        <Container>
          <FormContainer>
            <TitleContainer>
              <Title>
                <FormattedMessage id="search_associated_service_providers" />
              </Title>
              <GoBackContainer>
                <LinkButton onClick={() => history.push("/dashboard")}>
                  <Badge
                    color="#002244"
                    text={intl.formatMessage({ id: "goback" })}
                    fontSize="12px"
                  />
                </LinkButton>
              </GoBackContainer>
            </TitleContainer>
            <FieldsContainer>
              <Form ref={formRef} onSubmit={handleSubmit}>
                <Horizontal>
                  <SelectWithLabel
                    name="event"
                    options={
                      events &&
                      events.map((value) => {
                        return {
                          label: value.eventName,
                          value: value.id,
                        };
                      })
                    }
                    clearable
                    label={intl.formatMessage({ id: "event_edition" })}
                    placeholder={intl.formatMessage({ id: "event_edition" })}
                  />

                  <SelectWithLabel
                    name="serviceType"
                    options={services}
                    clearable
                    label={intl.formatMessage({ id: "service_type" })}
                    placeholder={intl.formatMessage({ id: "service_type" })}
                  />
                </Horizontal>
                <Horizontal>
                  <InputWithLabel
                    name="booth"
                    label={intl.formatMessage({ id: "booth" })}
                  />
                  <InputWithLabel
                    name="exhibitor"
                    label={intl.formatMessage({ id: "exhibitor" })}
                  />
                </Horizontal>
                <Horizontal>
                  <FilterActionsContainer>
                    <SubmitContainer>
                      <FormButton
                        color="#C9C9C9"
                        type="button"
                        onClick={() => clearAll()}
                      >
                        <FormattedMessage id="clear_fields" />
                      </FormButton>
                    </SubmitContainer>
                    <SubmitContainer>
                      <FormButton type="submit" loading={loading}>
                        <FormattedMessage id="search" />
                      </FormButton>
                    </SubmitContainer>
                  </FilterActionsContainer>
                </Horizontal>
              </Form>
            </FieldsContainer>
          </FormContainer>
          <Table data={filteredData} columns={columns} hasPagination />
        </Container>
      </AnimatedMainContainer>
    </>
  );
};

export default ServiceProviderAssociation;
